import { Input, Modal, Tooltip } from "antd";
import moment from "moment";
import { Fragment, useRef, useState } from "react";
import { BiPencil } from "react-icons/bi";
import {
  FaArrowRight,
  FaExpandArrowsAlt,
  FaInfoCircle,
  FaRegCircle,
} from "react-icons/fa";
import { MdBlock, MdTrendingDown, MdTrendingUp } from "react-icons/md";
import { TiArrowSortedDown } from "react-icons/ti";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as Tip,
  XAxis,
  YAxis,
} from "recharts";
import {
  getNeutralBonus,
  getNeutralReport,
  updateNeutralClient,
  updateNeutralObservation,
} from "../../../api";
import _ from "../../../i18n";
import {
  formatAmount,
  formatDate,
  getMonthShortLabelObject,
  renderDurationMinutesDisplay,
  renderTeamYears,
  useOnClickOutside,
} from "../../../utils";
import TTPFilterHorizontalBar from "../../common/Filter/TTPFilterHorizontalBar";
import { IconClose } from "../../common/Icons";

const { TextArea } = Input;

export default function NeutralReport({
  collaboratorOptions,
  organization,
  token,
  lng,
  onClose,
}) {
  const ref = useRef();
  useOnClickOutside(ref, () => setYearDivIsVisible(false));
  const [data, setData] = useState([]);
  const [open, setOpenModal] = useState(false);
  const [client, setClient] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [yearDivIsVisible, setYearDivIsVisible] = useState(false);
  const [filter, setFilter] = useState({
    collaborator: collaboratorOptions[0],
    tabs: getMonthShortLabelObject(lng),
    period: getMonthShortLabelObject(lng)[new Date().getMonth() - 1],
    start: getMonthShortLabelObject(lng)[new Date().getMonth() - 1].start,
    end: getMonthShortLabelObject(lng)[new Date().getMonth() - 1].end,
  });

  const handleMonthChange = (tab) => {
    setFilter({
      ...filter,
      period: tab,
      start: tab.start,
      end: tab.end,
    });
  };

  let getNeutralReportQuery = useQuery(
    ["getNeutralReportQuery", token, organization, filter, year],
    async () => {
      if ((token, organization, filter.collaborator, year)) {
        try {
          const response = await getNeutralReport(
            token,
            organization.id,
            filter.collaborator.value,
            year,
            filter.start,
            filter.end
          );

          const data = response.data.data;
          setData(data);
          return data;
        } catch (e) {
          console.error(e);
          setData([]);
          return [];
        }
      }
    },
    {
      enabled: !!token && !!organization && !!filter && !!year,
    }
  );

  const handleSave = (collaboratorId, client) => {
    const month = filter.start
      ? new Date(filter.start).getMonth() + 1
      : new Date().getMonth() + 1;
    const neutral = {
      [collaboratorId]: {
        [client.id]: !client.neutralized,
      },
    };
    const payload = {
      token,
      organization: organization && organization.id,
      year,
      month,
      neutral: JSON.stringify(neutral),
    };

    updateNeutralClient(payload)
      .then((resp) => {
        if (resp.data.result === "OK") {
          toast.success(_("successfully_updated"));
        }
      })
      .catch((e) => {
        if (
          e.response &&
          e.response.data &&
          e.response.data.errors &&
          e.response.data.errors.length > 0
        )
          toast.error(_(e.response.data.errors[0].message));
        else toast.error(_("error"));
      })
      .finally(() => {
        const newData = data.map((c) =>
          c.id === client.id ? { ...c, neutralized: !c.neutralized } : { ...c }
        );
        setData(newData);
        if (open) {
          setOpenModal(!open);
        }
      });
  };

  const renderYearSelect = () => {
    return (
      <div
        ref={ref}
        className={"customed_select"}
        style={{
          position: "relative",
          background: "#6d7f92",
          color: "#ffffff",
          zIndex: 9999,
        }}
      >
        <div
          className={"customed_select_header"}
          onClick={() => setYearDivIsVisible(!yearDivIsVisible)}
        >
          <span>{year}</span>
          <div className={"actions"}>
            <div>
              <span
                className={"action"}
                style={{
                  transform: yearDivIsVisible ? "rotate(180deg)" : "",
                  transition: "transform .2s ease-in-out",
                }}
              >
                <TiArrowSortedDown />
              </span>
            </div>
          </div>
        </div>
        {yearDivIsVisible && (
          <div
            className={"customed_select_body"}
            style={{
              position: "absolute",
              top: "99%",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 999,
            }}
          >
            <div className={"customed_select_body_options"}>
              {renderTeamYears().map((item, index) => (
                <div
                  key={index}
                  className={"customed_select_body_option"}
                  onClick={() => setYear(item)}
                  style={{ zIndex: 4 }}
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleCollaboratorsChange = (item) => {
    const selected = collaboratorOptions.filter(
      (c) => c.value === parseInt(item)
    );
    setFilter({ ...filter, collaborator: selected[0] });
  };

  const filters = [
    {
      type: "SELECT",
      options: {
        isSimple: true,
        isSearch: true,
        isMulti: false,
        isClearable: true,
        placeholder: _("collaborator"),
        options: collaboratorOptions,
        values: filter.collaborator.value,
        action: handleCollaboratorsChange,
      },
    },
  ];

  const renderFilterFrame = () => {
    let countFilter = 0;
    filters.forEach((filter) => {
      if (filter && filter.options.values) {
        countFilter++;
      }
    });

    return (
      <div>
        <div
          className="principal-line"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <h3 style={{ width: "50%" }}>{filter.collaborator.label}</h3>

          <TTPFilterHorizontalBar
            button
            bg="#FFF"
            subBG="F8F9FA"
            filter={filter}
            filters={filters}
            countFilter={countFilter}
            pageSize={filter.pageSize}
            updateFilterContent={setFilter}
            customAddOn={renderYearSelect()}
            cssClass="filter-horizontal-button"
          />
        </div>
      </div>
    );
  };

  const renderSubFilterFrame = () => {
    return (
      <div className="filter-frame">
        <div
          className="additional-line"
          style={{
            display: "flex",
            justifyContent: "flex-start",
            overflowX: "auto",
          }}
        >
          {filter.tabs.map((tab) => {
            return (
              <div
                className={`tag ${
                  filter.period.id === tab.id ? "active" : ""
                } ${
                  filter.period &&
                  moment().year() == year &&
                  tab.id > moment().month() + 1 &&
                  "disabled"
                }`}
                onClick={() =>
                  filter.period &&
                  moment().year() == year &&
                  tab.id > moment().month() + 1
                    ? null
                    : handleMonthChange(tab)
                }
              >
                {tab.label}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const handleOpenModal = (id) => {
    const clientData = data.find((c) => c.id === id);
    setClient(clientData);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setClient(null);
  };

  return (
    <Fragment>
      <div
        style={{
          width: 900,
          height: "100%",
          position: "absolute",
          right: 0,
          background: "white",
          // paddingTop: 35,
        }}
      >
        <div className="sidebar_header">
          <div className="sidebar_header_infos">
            <h3>{_("neutral")}</h3>
          </div>
          <div className="sidebar_header_action" onClick={onClose}>
            <IconClose size={12} />
          </div>
        </div>
        <div className="sidebar_body vertical_content">
          {renderFilterFrame()}
          {renderSubFilterFrame()}
          <div className="sidebar_body_content">
            {getNeutralReportQuery.isFetching ? (
              <div className="ripple_loading">
                <img src={"/img/Ripple-1s-150px.svg"} alt="" />
              </div>
            ) : getNeutralReportQuery.isFetched && data.length > 0 ? (
              <div className="neutral_card_container">
                {data.map((client, index) => (
                  <div
                    className={`neutral_card ${index < 2 && `main`}`}
                    key={client.id}
                  >
                    <div className="neutral_card_header">
                      <h3>
                        {client.name}
                        {client.decision ? (
                          <Tooltip placement="top" title={client.decision}>
                            <FaInfoCircle color="#6d7f92" />
                          </Tooltip>
                        ) : null}
                      </h3>
                      <div>
                        <Tooltip
                          title={`(${formatAmount(client.fees / 12)} * ${
                            client.encoding.months
                          } * ${0.1667}) / ${client.actifColl}`}
                          placement="top"
                        >
                          <div
                            className="fees"
                            style={{
                              backgroundColor:
                                client.bonus >= 500 || client.bonus <= 0
                                  ? "#de484850"
                                  : "#02af8e50",
                            }}
                          >
                            {formatAmount(client.bonus)}
                          </div>
                        </Tooltip>
                        <div
                          className="expand_icon"
                          onClick={() => handleOpenModal(client.id)}
                        >
                          <FaExpandArrowsAlt />
                        </div>
                      </div>
                    </div>

                    <div className="block">
                      <div>
                        <div>
                          <strong>{client.encoding.months}</strong>
                        </div>
                        <span>{_("month")}</span>
                      </div>
                      <div>
                        <div>
                          <strong>{client.actifColl}</strong>
                        </div>
                        <span>{_("active_col_month")}</span>
                      </div>
                      <div>
                        <div>
                          <strong>{formatAmount(client.billing)}</strong>
                        </div>
                        <span>{_("billing")}</span>
                      </div>
                      <div>
                        <div>
                          <strong>
                            {renderDurationMinutesDisplay(client.real)}
                          </strong>
                        </div>
                        <span>{_("real")}</span>
                      </div>
                    </div>
                    <div
                      className="block btn"
                      style={{
                        background: client.neutralized ? "#06d9b1" : "#29394d",
                      }}
                      onClick={() =>
                        handleSave(filter.collaborator.value, client)
                      }
                    >
                      {client.neutralized ? (
                        <Tooltip title={_("liberate")} className="btn">
                          <FaRegCircle height={10} width={10} />
                          <span>{_("liberate")}</span>
                        </Tooltip>
                      ) : (
                        <Tooltip title={_("neutralize")} className="btn">
                          <MdBlock height={10} width={10} />
                          <span>{_("neutralize")}</span>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no_data">
                <div className="title">{_("noDataFound")}</div>
                <div className="subTitle">{_("noReportingsFound")}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {open && (
        <NeutralModal
          token={token}
          organization={organization}
          collaborator={filter.collaborator.value}
          client={client}
          filter={filter}
          language={lng}
          year={year}
          onClose={handleCloseModal}
          action={() => handleSave(filter.collaborator.value, client)}
        />
      )}
    </Fragment>
  );
}

export const NeutralModal = ({
  token,
  organization,
  collaborator,
  client,
  language,
  filter,
  year,
  onClose,
  action,
}) => {
  const [open, setOpen] = useState(false);
  const [rowId, setRowId] = useState(null);
  const [data, setData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [observation, setObservation] = useState("");

  let getNeutralBonusQuery = useQuery(
    ["getNeutralBonusQuery", token, organization, client, collaborator, year],
    async () => {
      if ((token, organization, client, collaborator, year)) {
        try {
          const response = await getNeutralBonus(
            token,
            organization.id,
            collaborator,
            client.id,
            year,
            filter.start,
            filter.end
          );

          const data = response.data.data[0];
          setChartData(
            data.children.map((elem) => ({
              name: elem.month.label,
              prime: parseFloat(elem.bonus),
              encodingFee: parseFloat(elem.encodingFee),
              billing: parseFloat(elem.billing),
            }))
          );
          setObservation(
            Object.fromEntries(
              data.children.map((elem) => [elem.key, elem.observation || ""])
            )
          );
          setData(data);
          return data;
        } catch (e) {
          console.error(e);
          setData([]);
          return [];
        }
      }
    },
    {
      enabled:
        !!token && !!organization && !!client && !!collaborator && !!year,
    }
  );

  const handleObservationChange = (id, value) => {
    setObservation({
      ...observation,
      [id]: value,
    });
  };

  const handleUpdateNeutral = (id) => {
    const payload = {
      token,
      organizationId: organization && organization.id,
      neutralId: id,
      observation: observation[id],
    };

    updateNeutralObservation(payload)
      .then((resp) => {
        if (resp.data.result === "OK") {
          toast.success(_("successfully_updated"));
        }
      })
      .catch((e) => {
        if (
          e.response &&
          e.response.data &&
          e.response.data.errors &&
          e.response.data.errors.length > 0
        )
          toast.error(_(e.response.data.errors[0].message));
        else toast.error(_("error"));
      })
      .finally(() => {
        setOpen(false);
        setRowId(null);
      });
  };

  return (
    <div className="neutral_modal">
      <Modal
        open={open}
        onOk={() => handleUpdateNeutral(rowId)}
        onCancel={() => setOpen(false)}
      >
        <TextArea
          rows={4}
          placeholder="Saisir votre obsérvation..."
          value={observation[rowId]}
          onChange={(e) => handleObservationChange(rowId, e.target.value)}
          maxLength={200}
        />
      </Modal>
      <div className="neutral_modal_header">
        <h3>{client.name}</h3>
        {client.decision ? (
          <Tooltip placement="top" title={client.decision}>
            <FaInfoCircle color="#6d7f92" />
          </Tooltip>
        ) : null}
        <div className="neutral_modal_close" onClick={onClose}>
          <IconClose size={12} />
        </div>
      </div>
      <div className="neutral_modal_body">
        <div>
          <div>
            <h4>{_("monthlyRecap")}</h4>
            <span>
              {formatDate(year, filter.start)}
              <FaArrowRight size={10} />
              {formatDate(year, filter.end)}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              width: "100%",
            }}
          >
            <div className="block">
              <div
                className="child"
                style={{
                  width: "calc(50% - 5px)",
                  backgroundColor: "#18a0fb50",
                  color: "#29394d",
                }}
              >
                <div>
                  <strong>{formatAmount(client.fees / 12)}</strong>
                </div>
                <span>{_("fees")}</span>
              </div>
              <div
                className="child"
                style={{
                  background: "transparent",
                  boxShadow: "none",
                  width: "calc(50% - 5px)",
                }}
              >
                <div className="sub-child">
                  <div>
                    <strong>{formatAmount(client.encoding.fee)}</strong>
                  </div>
                  <span>{_("encodingFee")}</span>
                </div>
                <div className="sub-child">
                  <div>
                    <strong>{formatAmount(client.bonus)}</strong>
                  </div>
                  <span>{_("Prime")}</span>
                </div>
              </div>
            </div>
            <div className="block">
              <div className="child">
                {client.encoding.nonCoda > 0 && (
                  <Tooltip
                    placement="top"
                    title={`${client.encoding.nonCoda} Non Coda`}
                    className="info-tip"
                  >
                    <FaInfoCircle color="#6d7f92" size={12} />
                  </Tooltip>
                )}
                <div>
                  <strong>{client.encoding.months}</strong>
                </div>
                <span>{_("month_enc")}</span>
              </div>
              <div className="child">
                <div>
                  <strong>{client.actifColl}</strong>
                </div>
                <span>{_("active_col_month")}</span>
              </div>
              <div className="child">
                <div>
                  <strong>{formatAmount(client.billing)}</strong>
                </div>
                <span>{_("billing")}</span>
              </div>
              <div className="child">
                <div>
                  <strong>{renderDurationMinutesDisplay(client.real)}</strong>
                </div>
                <span>{_("real")}</span>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div>
            <h4>{_("annualOverview")}</h4>
            <span>
              {formatDate(year, "01-01")}
              <FaArrowRight size={10} />
              {formatDate(
                year,
                getMonthShortLabelObject(language)[new Date().getMonth()].end
              )}
            </span>
          </div>
          {getNeutralBonusQuery.isFetching ? (
            <div className="ripple_loading">
              <img src={"/img/Ripple-1s-150px.svg"} alt="ripple-svg" />
            </div>
          ) : getNeutralBonusQuery.isFetched ? (
            <Fragment>
              <div className="chart-view">
                <div
                  style={{
                    padding: "1rem",
                    backgroundColor: "#f1f2f4",
                    borderRadius: 5,
                    height: 300,
                    width: 800,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      width={500}
                      height={300}
                      data={chartData}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 0"
                        vertical={false}
                        stroke="#64748B10"
                      />
                      <XAxis dataKey="name" />
                      <YAxis
                        axisLine={false}
                        type="number"
                        tickLine={false}
                        tickSize={10}
                        unit="€"
                        tickMargin={20}
                        fill="#64748B"
                      />
                      <Tip
                        content={<CustomTooltip />}
                        cursor={{ fill: "rgba(225, 228, 232, 0.50)" }}
                      />
                      <Legend
                        formatter={(value) => {
                          return _(value);
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="prime"
                        stroke="#8884d8"
                        strokeWidth={2}
                        activeDot={{ r: 5 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="encodingFee"
                        stroke="#E78B48"
                        strokeWidth={2}
                        activeDot={{ r: 5 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="billing"
                        stroke="#1DCD9F"
                        strokeWidth={2}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="details">
                  <div className="child">
                    <div
                      style={{
                        borderRadius: 3,
                        width: "100%",
                        textAlign: "center",
                        backgroundColor: "#F1F2F4",
                      }}
                    >
                      {formatAmount(data.annualFees)}
                    </div>
                    <span>{_("annualFees")}</span>
                  </div>
                  <div className="child">
                    <div
                      style={{
                        borderRadius: 3,
                        width: "100%",
                        textAlign: "center",
                        backgroundColor:
                          parseFloat(data.cumulTotal) >
                          parseFloat(data.annualFees)
                            ? "#de484850"
                            : "#02af8e50",
                      }}
                    >
                      {formatAmount(data.cumulTotal)}
                    </div>
                    <span>{_("cumulTotal")}</span>
                  </div>
                  <div className="child">
                    <div
                      style={{
                        borderRadius: 3,
                        width: "100%",
                        textAlign: "center",
                        backgroundColor: "#F1F2F4",
                      }}
                    >
                      {formatAmount(data.sumBilling)}
                    </div>
                    <span>{_("billingTotal")}</span>
                  </div>
                  <div className="child">
                    <Tooltip
                      placement="top"
                      title={`${formatAmount(client.bonus)} - ${formatAmount(
                        data.sumBilling
                      )}`}
                    >
                      <div
                        style={{
                          borderRadius: 3,
                          width: "100%",
                          textAlign: "center",
                          backgroundColor: "#F1F2F4",
                        }}
                      >
                        {formatAmount(client.bonus - data.sumBilling)}
                      </div>
                    </Tooltip>
                    <span>{_("diff")}</span>
                  </div>
                </div>
                <div className="details">
                  <div className="child">
                    <div
                      style={{
                        borderRadius: 3,
                        width: "100%",
                        textAlign: "center",
                        backgroundColor: "#F1F2F4",
                      }}
                    >
                      {formatAmount(data.sumDiff)}
                    </div>
                    <span>{_("encodingBonusSum")}</span>
                  </div>
                  <div className="child">
                    <div
                      style={{
                        borderRadius: 3,
                        width: "100%",
                        textAlign: "center",
                        backgroundColor: "#F1F2F4",
                      }}
                    >
                      {formatAmount(data.sumBillingOver12)}
                    </div>
                    <span>{_("sumBillingOver12")}</span>
                  </div>
                  <div className="child">
                    <Tooltip
                      placement="top"
                      title={`(${formatAmount(client.bonus)} - ${formatAmount(
                        data.sumBilling
                      )}) - ${formatAmount(data.sumDiff)}`}
                    >
                      <div
                        style={{
                          borderRadius: 3,
                          width: "100%",
                          textAlign: "center",
                          backgroundColor: "#F1F2F4",
                        }}
                      >
                        {formatAmount(
                          client.bonus - data.sumBilling - data.sumDiff
                        )}
                      </div>
                    </Tooltip>
                    <span>{_("-")}</span>
                  </div>
                </div>
              </div>
              <table className="table-view">
                <thead>
                  <tr>
                    <th>{_("month")}</th>
                    <th>{_("prime")}</th>
                    <th>{_("month_enc")}</th>
                    <th>{_("cumul_month_enc")}</th>
                    <th>{_("billing")}</th>
                    <th>{_("encodingFee")}</th>
                    <th>{_("encodingBonus")}</th>
                    <th>{_("max12")}</th>
                    <th>{_("cumulTotal")}</th>
                    <th>{_("observation")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.children.map((elem, index) => (
                    <tr
                      key={`${index}-${elem.key}`}
                      className={`${
                        filter.start.split("-")[0] ==
                        elem.month.value.split("/")[1]
                          ? "active"
                          : ""
                      }`}
                    >
                      <td>{_(elem.month.label)}</td>
                      <td>{formatAmount(elem.bonus)}</td>
                      <td>{elem.encodingMonths}</td>
                      <td>{elem.cumulEncodingMonths}</td>
                      <td>{formatAmount(elem.billing)}</td>
                      <td>{formatAmount(elem.encodingFee)}</td>
                      <td
                        className={`${
                          elem.cumulEncodingMonths > 12 ? "overline" : ""
                        }`}
                      >
                        <span>{formatAmount(elem.diff)}</span>
                        {elem.cumulEncodingMonths > 12 && <strong>0€</strong>}
                      </td>
                      <td className="max12">
                        <div
                          style={{
                            "--bg-color":
                              elem.max12Months <= 0 ? "#de484830" : "#02af8e30",
                            "--color":
                              elem.max12Months <= 0 ? "#de4848" : "#02af8e",
                          }}
                        >
                          <span>{formatAmount(elem.max12Months)}</span>
                          {elem.max12Months <= 0 ? (
                            <MdTrendingDown size={12} />
                          ) : (
                            <MdTrendingUp size={12} />
                          )}
                        </div>
                      </td>
                      <td>{formatAmount(elem.cumulTotal)}</td>
                      <td className="observation">
                        <div>
                          <Tooltip
                            placement="top"
                            title={observation[elem.key]}
                          >
                            <p>
                              {observation[elem.key]?.substring(0, 10)}
                              {observation[elem.key]?.length > 10 ? "..." : ""}
                            </p>
                          </Tooltip>
                          <div
                            onClick={() => {
                              setRowId(elem.key);
                              setOpen(true);
                            }}
                            className="edit-icon"
                          >
                            <BiPencil fill="#29394d66" size={20} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Fragment>
          ) : (
            <div className="no_data">
              <div className="title">{_("noDataFound")}</div>
            </div>
          )}
        </div>
      </div>

      <div
        className="neutral_modal_footer"
        style={{
          background: client.neutralized ? "#06d9b1" : "#29394d",
        }}
        onClick={action}
      >
        {client.neutralized ? (
          <Tooltip title={_("liberate")} className="btn">
            <FaRegCircle height={10} width={10} />
            <span>{_("liberate")}</span>
          </Tooltip>
        ) : (
          <Tooltip title={_("neutralize")} className="btn">
            <MdBlock height={10} width={10} />
            <span>{_("neutralize")}</span>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;

  const { name } = payload[0].payload;

  return (
    <div className="custom_tooltip">
      <div className="custom_tooltip_child">{_(name)}</div>
      {payload.map((item) => (
        <div key={item.name} className="custom_tooltip_child">
          <div>
            <div className="square" style={{ backgroundColor: item.color }} />
            <span>{_(item.name)}</span>
          </div>
          <div>{formatAmount(item.value)}</div>
        </div>
      ))}
    </div>
  );
};
